import { z } from 'zod';
import {
  DATASET_TRIGGER_CRITERIA,
  DATASET_TRIGGER_RUN_MODES,
} from '../resource/resource.types';
import { workflowNodeInputSchema } from '../workflows/workflow.types';
import type { ApiSchemaClass, ApiSchemaDefinition } from './types';
import { okTrueResponseSchema } from './common-responses';

/**
 * Registered webhook provider keys. This is the API contract's copy of the
 * server-side webhook-providers registry (`WEBHOOK_PROVIDER_KEYS` in
 * `apps/server/.../webhook-providers/types.ts`). A drift-guard test in the
 * server (`webhook-provider-key-schema.spec.ts`) asserts this list stays in
 * sync with the providers actually registered in the resolver, so both stay
 * in lockstep and new providers can't silently 400 at the API boundary.
 */
export const WEBHOOK_PROVIDER_KEYS = [
  'generic',
  'email',
  'hubspot',
  'github',
  'zendesk',
  'telegram',
  'sentry',
  'linear',
  'workos',
  'calendly',
  'shopify',
  'stripe',
  'typeform',
  'grain',
  'gong',
  'lemlist',
  'klaviyo',
  'shipbob',
] as const;

const webhookProviderKeySchema = z.enum(WEBHOOK_PROVIDER_KEYS);

/**
 * The canonical webhook provider key type. This — together with
 * {@link WEBHOOK_PROVIDER_KEYS} and the guards below — is the single source of
 * truth every layer (server registry, API contract, client) must derive from,
 * so a new provider added to the list above is understood everywhere at once
 * rather than needing hand-maintained string lists that silently drift.
 */
export type WebhookProviderKey = (typeof WEBHOOK_PROVIDER_KEYS)[number];

export function isWebhookProviderKey(
  value: unknown
): value is WebhookProviderKey {
  return (
    typeof value === 'string' &&
    (WEBHOOK_PROVIDER_KEYS as readonly string[]).includes(value)
  );
}

/**
 * Coerces an arbitrary value to a known provider key, falling back to `generic`
 * for anything unrecognized (unknown/legacy stored keys, malformed input).
 */
export function parseWebhookProviderKey(value: unknown): WebhookProviderKey {
  return isWebhookProviderKey(value) ? value : 'generic';
}

// ---------------------------------------------------------------------------
// Request body / query Zod schemas
// ---------------------------------------------------------------------------

export const workflowGraphCreateNodeSchema = z.object({
  id: z.string().uuid(),
  node: workflowNodeInputSchema,
  inputShape: z.string().nullable().optional(),
});
export type WorkflowGraphCreateNode = z.infer<
  typeof workflowGraphCreateNodeSchema
>;

export const workflowGraphCreateEdgeSchema = z.object({
  fromNodeId: z.string().uuid(),
  toNodeId: z.string().uuid(),
});
export type WorkflowGraphCreateEdge = z.infer<
  typeof workflowGraphCreateEdgeSchema
>;

export const workflowGraphCreateTriggerSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('cron'),
    name: z.string(),
    schedule: z.string(),
    timezone: z.string().optional(),
    defaultInput: z.unknown().optional(),
    /**
     * Whether the workflow ↔ trigger *binding* is live — the per-workflow on/off
     * switch on the canvas card. Defaults to true.
     *
     * Deliberately the binding and not the trigger row: cron/webhook/event rows
     * are deduped org-wide on `identity_key`, so two workflows on the same
     * schedule share one `workflow_triggers_cron` row. Disabling the row would
     * stop the other workflow too. A graph save deletes and re-inserts every
     * binding, so this has to travel with the trigger or the save would flip a
     * disabled trigger back on.
     */
    enabled: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal('webhook'),
    name: z.string(),
    providerKey: webhookProviderKeySchema.optional(),
    verificationConfig: z.unknown().optional(),
    eventKey: z.string().optional(),
    eventConfig: z.unknown().optional(),
    endpointSecret: z.string().min(1).optional(),
    defaultInput: z.unknown().optional(),
    /** Per-workflow binding on/off. See the `cron` variant. Defaults to true. */
    enabled: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal('event'),
    name: z.string(),
    eventName: z.string(),
    defaultInput: z.unknown().optional(),
    /** Per-workflow binding on/off. See the `cron` variant. Defaults to true. */
    enabled: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal('dataset'),
    name: z.string(),
    datasetId: z.string().uuid(),
    criterion: z.enum(DATASET_TRIGGER_CRITERIA),
    /** Omit or null to watch every non-internal column. */
    trackFields: z.array(z.string()).nullable().optional(),
    filter: z.unknown().optional(),
    /**
     * Fire for rows that already exist at activation. Defaults to false, which
     * primes the ledger instead so adding a trigger to a populated dataset does
     * not storm the run queue.
     */
    processExistingRows: z.boolean().optional(),
    /**
     * `manual` binds the workflow to the dataset without ever firing it on its
     * own — runs come from the dataset grid's Run menu instead. Defaults to
     * `automatic`, which is what a trigger normally means.
     */
    runMode: z.enum(DATASET_TRIGGER_RUN_MODES).optional(),
    defaultInput: z.unknown().optional(),
    /** Per-workflow binding on/off. See the `cron` variant. Defaults to true. */
    enabled: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal('existing'),
    triggerId: z.string().uuid(),
    webhook: z
      .object({
        name: z.string().optional(),
        webhookRowEnabled: z.boolean().optional(),
        providerKey: webhookProviderKeySchema.optional(),
        verificationConfig: z.unknown().optional(),
        eventKey: z.string().optional(),
        eventConfig: z.unknown().optional(),
      })
      .optional(),
    defaultInput: z.unknown().optional(),
    /** Per-workflow binding on/off. See the `cron` variant. Defaults to true. */
    enabled: z.boolean().optional(),
  }),
]);
export type WorkflowGraphCreateTrigger = z.infer<
  typeof workflowGraphCreateTriggerSchema
>;

export const workflowGraphCreateInputSchema = z.object({
  nodes: z.array(workflowGraphCreateNodeSchema),
  edges: z.array(workflowGraphCreateEdgeSchema),
  /** @deprecated use `triggers` for workflows with multiple trigger bindings. */
  trigger: workflowGraphCreateTriggerSchema.optional(),
  triggers: z.array(workflowGraphCreateTriggerSchema).optional(),
});
export type WorkflowGraphCreateInput = z.infer<
  typeof workflowGraphCreateInputSchema
>;

/** Workflow-level dedupe config: resolve a key from run inputs before starting a run. */
export const workflowIdempotencyKeySchema = z.union([
  z.object({
    strategy: z.literal('value'),
    pointer: z.string().min(1),
  }),
  z.object({
    strategy: z.literal('value'),
    template: z.string().min(1),
  }),
]);
export type WorkflowIdempotencyKey = z.infer<
  typeof workflowIdempotencyKeySchema
>;

export const workflowCreateBodySchema = z.object({
  name: z.string(),
  folderId: z.string().optional(),
  workflowStatus: z.string().optional(),
  graph: workflowGraphCreateInputSchema.optional(),
  idempotencyKey: workflowIdempotencyKeySchema.nullable().optional(),
});
export type WorkflowCreateBody = z.infer<typeof workflowCreateBodySchema>;

export const workflowUpdateBodySchema = z.object({
  name: z.string().optional(),
  workflowStatus: z.string().optional(),
  graph: workflowGraphCreateInputSchema.optional(),
  idempotencyKey: workflowIdempotencyKeySchema.nullable().optional(),
});
export type WorkflowUpdateBody = z.infer<typeof workflowUpdateBodySchema>;

export const workflowScheduledAgentUpdateBodySchema = z.object({
  name: z.string().optional(),
  schedule: z.string().optional(),
  timezone: z.string().optional(),
  agentId: z.string().optional(),
  prompt: z.string().optional(),
});
export type WorkflowScheduledAgentUpdateBody = z.infer<
  typeof workflowScheduledAgentUpdateBodySchema
>;

export const workflowListRunsQuerySchema = z.object({
  limit: z.coerce.number().optional(),
  offset: z.coerce.number().optional(),
  status: z.string().optional(),
  createdAfter: z.string().optional(),
  createdBefore: z.string().optional(),
  /** Comma-separated `trigger_type`s to include (e.g. `bucket_flush`); omit for all. */
  triggerType: z.string().optional(),
});
export type WorkflowListRunsQuery = z.infer<typeof workflowListRunsQuerySchema>;

export const workflowRunStatsQuerySchema = z.object({
  status: z.string().optional(),
  createdAfter: z.string().optional(),
  createdBefore: z.string().optional(),
  /** Comma-separated `trigger_type`s to include; omit for all. */
  triggerType: z.string().optional(),
  /** Time bucket size for the series; defaults to 'day'. */
  bucket: z.enum(['hour', 'day']).optional(),
});
export type WorkflowRunStatsQuery = z.infer<typeof workflowRunStatsQuerySchema>;

export const datasetAutomationOverlayQuerySchema = z.object({
  /**
   * The artifact the caller has loaded. A run recorded against a different
   * artifact still counts — the row may not have changed — so this narrows
   * nothing; it is what lets the response say whether a run is current.
   */
  artifactId: z.string().optional(),
});
export type DatasetAutomationOverlayQuery = z.infer<
  typeof datasetAutomationOverlayQuerySchema
>;

export const workflowAddTriggerBodySchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('cron'),
    name: z.string().optional(),
    // Set only on the atomic "new trigger" create: when the workflow id doesn't
    // exist yet, the server mints it to hold this trigger in the same request
    // (and places it in this folder, if given) instead of a separate
    // workflow.create call — so the workflow is never observable without its
    // trigger, which otherwise flashes it under "Standalone Workflows".
    folderId: z.string().uuid().optional(),
    schedule: z.string().optional(),
    timezone: z.string().optional(),
    priority: z.number().optional(),
    defaultInput: z.unknown().optional(),
    enabled: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal('webhook'),
    name: z.string().optional(),
    folderId: z.string().uuid().optional(),
    priority: z.number().optional(),
    defaultInput: z.unknown().optional(),
    enabled: z.boolean().optional(),
    providerKey: webhookProviderKeySchema.optional(),
    verificationConfig: z.unknown().optional(),
    eventKey: z.string().optional(),
    eventConfig: z.unknown().optional(),
    endpointSecret: z.string().min(1).optional(),
  }),
  z.object({
    kind: z.literal('event'),
    name: z.string().optional(),
    folderId: z.string().uuid().optional(),
    eventName: z.string(),
    priority: z.number().optional(),
    defaultInput: z.unknown().optional(),
    enabled: z.boolean().optional(),
  }),
]);
export type WorkflowAddTriggerBody = z.infer<
  typeof workflowAddTriggerBodySchema
>;

export const workflowUpdateTriggerBodySchema = z.object({
  // The binding row id in the URL churns on every graph save (each save
  // DELETEs + re-INSERTs the workflow's bindings with fresh ids), so a client
  // holding a slightly stale draft would 404 with "Trigger binding not found".
  // The trigger id is stable, so send it too and let the server fall back to
  // resolving the binding by (workflowId, triggerId), which is unique.
  triggerId: z.string().optional(),
  name: z.string().optional(),
  schedule: z.string().optional(),
  timezone: z.string().optional(),
  bindingEnabled: z.boolean().optional(),
  cronRowEnabled: z.boolean().optional(),
  webhookRowEnabled: z.boolean().optional(),
  eventRowEnabled: z.boolean().optional(),
  providerKey: webhookProviderKeySchema.optional(),
  verificationConfig: z.unknown().optional(),
  eventKey: z.string().optional(),
  eventConfig: z.unknown().optional(),
  endpointSecret: z.string().min(1).optional(),
  eventName: z.string().optional(),
  priority: z.number().optional(),
  defaultInput: z.unknown().optional(),
  // Dataset triggers only. `manual` never fires on its own; sending
  // `automatic` switches it back to firing on the changes it detects, from here
  // on. The baseline is kept current while manual, so no backfill of the gap.
  runMode: z.enum(DATASET_TRIGGER_RUN_MODES).optional(),
});
export type WorkflowUpdateTriggerBody = z.infer<
  typeof workflowUpdateTriggerBodySchema
>;

export const workflowRenameTriggerBodySchema = z.object({
  name: z.string().min(1),
});
export type WorkflowRenameTriggerBody = z.infer<
  typeof workflowRenameTriggerBodySchema
>;

export const workflowBindTriggerBodySchema = z.object({
  priority: z.number().optional(),
  defaultInput: z.unknown().optional(),
  enabled: z.boolean().optional(),
});
export type WorkflowBindTriggerBody = z.infer<
  typeof workflowBindTriggerBodySchema
>;

export const workflowSummariesBodySchema = z.object({
  workflowIds: z.array(z.string()),
});
export type WorkflowSummariesBody = z.infer<typeof workflowSummariesBodySchema>;

export const workflowTriggerSummariesBodySchema = z.object({
  triggerIds: z.array(z.string()),
});

/**
 * Runs for many workflows in one window, for views that plot a whole schedule at
 * once. The per-workflow `GET :id/runs` would need a request per workflow, which
 * is a request per row on a page that exists to show many rows.
 */
export const workflowRunsByWorkflowsBodySchema = z.object({
  workflowIds: z.array(z.string()),
  /** ISO timestamp lower bound on created_at (inclusive). */
  createdAfter: z.string().optional(),
  /** ISO timestamp upper bound on created_at (inclusive). */
  createdBefore: z.string().optional(),
  /**
   * Cap on rows returned across all workflows combined. Constrained here rather
   * than only clamped downstream: a bare `z.number()` accepts a negative, which
   * reaches SQL as `LIMIT -n` and fails the query as a 500 instead of a 400.
   */
  limit: z.number().int().positive().optional(),
});
export type WorkflowRunsByWorkflowsBody = z.infer<
  typeof workflowRunsByWorkflowsBodySchema
>;
export type WorkflowTriggerSummariesBody = z.infer<
  typeof workflowTriggerSummariesBodySchema
>;

export const workflowTriggerBodySchema = z.object({
  runInput: z.unknown().optional(),
});
export type WorkflowTriggerBody = z.infer<typeof workflowTriggerBodySchema>;

export const workflowDraftTestRunBodySchema = z.object({
  graph: workflowGraphCreateInputSchema,
  runInput: z.unknown().optional(),
  name: z.string().optional(),
  triggerMode: z.literal('manual').optional(),
});
export type WorkflowDraftTestRunBody = z.infer<
  typeof workflowDraftTestRunBodySchema
>;

export const workflowDraftTestRunResponseSchema = z.object({
  runId: z.string().uuid(),
  workflowId: z.string().uuid(),
  ephemeralWorkflowId: z.string().uuid(),
});
export type WorkflowDraftTestRunResponse = z.infer<
  typeof workflowDraftTestRunResponseSchema
>;

// ---------------------------------------------------------------------------
// Response schemas
// ---------------------------------------------------------------------------

export const workflowDefinitionResponseSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  ownerId: z.string().uuid(),
  name: z.string(),
  status: z.enum(['active', 'archived']),
  workflowStatus: z.string().optional(),
  workflowType: z.string().optional(),
  idempotencyKey: workflowIdempotencyKeySchema.nullable().optional(),
  graph: z.unknown().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  schedule: z.string().optional(),
  timezone: z.string().optional(),
  cronTriggerId: z.string().uuid().optional(),
  agentId: z.string().uuid().nullable().optional(),
  agentTitle: z.string().nullable().optional(),
  prompt: z.string().optional(),
});

export const workflowListRunsResponseSchema = z.object({
  items: z.array(z.unknown()),
  total: z.number(),
});

export const workflowRunsByWorkflowsResponseSchema = z.object({
  items: z.array(z.unknown()),
  /**
   * True when `limit` clipped the result. Callers that read a missing run as
   * "this schedule didn't fire" must not do so when this is set.
   */
  truncated: z.boolean(),
});

/**
 * A column a workflow writes back onto the dataset row it ran for.
 *
 * Always empty today — no node writes dataset columns yet. It is declared here
 * so the client can build typed columns from the wiring alone rather than
 * inspecting workflow graphs, and so adding the producing node needs no API
 * change.
 */
export const datasetAutomationWriteSchema = z.object({
  columnName: z.string(),
  type: z.enum(['text', 'number', 'boolean', 'date', 'timestamp', 'unknown']),
});

export const datasetAutomationsResponseSchema = z.object({
  /**
   * The org that owns the dataset, when that differs from the reader's own
   * org — i.e. the dataset was shared in. Null on an unshared dataset.
   */
  ownerOrgName: z.string().nullable(),
  triggers: z.array(
    z.object({
      id: z.string(),
      name: z.string().nullable(),
      criterion: z.string(),
      /** `manual` triggers only ever fire from the dataset's own Run menu. */
      runMode: z.enum(DATASET_TRIGGER_RUN_MODES),
      enabled: z.boolean(),
      brokenReason: z.string().nullable(),
      /**
       * Whether the reader may fire this trigger, or only watch it.
       *
       * False on a dataset shared from another org for that org's own triggers:
       * their run state is part of reading the row, but firing them is not the
       * sharee's to do — and the workflow behind one is not theirs to open
       * either. Drives whether the grid draws run controls at all.
       */
      canRun: z.boolean(),
      workflows: z.array(
        z.object({
          id: z.string(),
          name: z.string(),
          status: z.string(),
          bindingEnabled: z.boolean(),
          writes: z.array(datasetAutomationWriteSchema),
        })
      ),
    })
  ),
});

export const datasetAutomationOverlayResponseSchema = z.object({
  /** One record per (row, workflow): that workflow's most recent run for it. */
  rows: z.array(
    z.object({
      datasetRowId: z.string().nullable(),
      rowKey: z.string(),
      workflowId: z.string(),
      triggerId: z.string(),
      runId: z.string(),
      status: z.string(),
      /**
       * False when the row has changed since this run saw it. Drives the
       * "Out of date" state; null when it cannot be determined (either side
       * is missing a fingerprint).
       */
      upToDate: z.boolean().nullable(),
      ranAt: z.string().nullable(),
      finishedAt: z.string().nullable(),
      /** Columns this run wrote back. Null until a write-back node exists. */
      outputs: z.record(z.string(), z.unknown()).nullable(),
      /**
       * The run's final node output, rendered as text for the grid cell — the
       * value the "expand workflow into a column" view shows for this row. Null
       * when the run produced no leaf output (still running, failed early, or a
       * leaf with no stored value), and null throughout for a reader who only
       * has `view` on the dataset: this is the one field carrying what a
       * workflow *produced* rather than the fact that it ran.
       */
      finalOutput: z.string().nullable(),
    })
  ),
  /**
   * True when the cap was hit and rows were dropped. Callers must not treat a
   * truncated overlay as authoritative for sorting or filtering.
   */
  truncated: z.boolean(),
});

/** Stops a bulk run, named by the group its runs were stamped with. */
export const datasetRunGroupCancelBodySchema = z.object({
  groupId: z.string(),
});
export type DatasetRunGroupCancelBody = z.infer<
  typeof datasetRunGroupCancelBodySchema
>;

export const datasetRunGroupCancelResponseSchema = z.object({
  /**
   * The dispatcher has been stopped. Cancelling the runs it already queued runs
   * asynchronously afterwards, so this does not carry a count.
   */
  stopped: z.boolean(),
});

/**
 * A batch and how far through it is.
 *
 * The counts are read from the batch's runs at the moment of asking rather than
 * accumulated, so they cannot drift from the runs themselves. A retried run
 * counts once, as its newest attempt.
 */
export const datasetRunGroupProgressSchema = z.object({
  groupId: z.string(),
  /** `detected` (something fired), `dispatch` (rows named), `replay`. */
  origin: z.string(),
  /** Why, in the same vocabulary a dataset-changed event uses. Null if unknown. */
  cause: z.string().nullable(),
  triggerId: z.string().nullable(),
  createdAt: z.string(),
  /** Runs in the batch, counting a retry chain once. */
  total: z.number(),
  /** Of those, the ones that have finished, however they finished. */
  terminal: z.number(),
  completed: z.number(),
  failed: z.number(),
  cancelled: z.number(),
  stopped: z.number(),
  pending: z.number(),
  /**
   * Whether more runs may still join.
   *
   * While true, `total` is a floor rather than a total and `percentComplete`
   * can go *down* as the batch grows — which is why `estimated` exists rather
   * than the client having to infer it.
   */
  dispatching: z.boolean(),
  /** Dispatch is closed and nothing is pending. */
  settled: z.boolean(),
  /** Set when a user stopped the batch, as distinct from it finishing. */
  stoppedAt: z.string().nullable(),
  settledAt: z.string().nullable(),
  /** `terminal / total` as a whole percent; 0 for a batch with no runs yet. */
  percentComplete: z.number(),
  /** True while dispatching: the denominator can still grow. */
  estimated: z.boolean(),
});
export type DatasetRunGroupProgress = z.infer<
  typeof datasetRunGroupProgressSchema
>;

/**
 * The batches on a dataset, newest first.
 *
 * What lets a reloaded tab — or a colleague — pick up a bulk run already in
 * flight, which was impossible while a batch lived only in the memory of the
 * tab that started it.
 */
export const datasetRunGroupsQuerySchema = z.object({
  /** Only batches still dispatching or still running. Default false. */
  activeOnly: z.coerce.boolean().optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});
export type DatasetRunGroupsQuery = z.infer<typeof datasetRunGroupsQuerySchema>;

export const datasetRunGroupsResponseSchema = z.object({
  groups: z.array(datasetRunGroupProgressSchema),
});

/**
 * Records rows a bulk run intends to fire, without firing any of them.
 *
 * Sent a page at a time, because the rows come from the grid's own snapshot and
 * the browser reads that a page at a time — materialising every id to send them
 * in one request is exactly what the grid's row cursor exists to avoid.
 *
 * Nothing runs until {@link DatasetRunGroupStartV20260601Schema}, so a caller
 * that stops half way through has fired nothing at all.
 */
export const datasetRunGroupCollectBodySchema = z.object({
  groupId: z.string(),
  triggerId: z.string(),
  /** Dataset row keys, as the caller's snapshot named them. */
  rowKeys: z.array(z.string()).min(1),
  /** Which bound workflows to fire. Omitted, every workflow on the trigger. */
  workflowIds: z.array(z.string()).min(1).optional(),
});
export type DatasetRunGroupCollectBody = z.infer<
  typeof datasetRunGroupCollectBodySchema
>;

export const datasetRunGroupCollectResponseSchema = z.object({
  /** Rows this page added. Lower than what was sent when a page is re-sent. */
  added: z.number(),
  /** Rows collected for this batch so far. */
  collected: z.number(),
});

/**
 * Says the set is complete and the batch should run.
 *
 * From here the server owns the dispatch: a saga drains the collected rows in
 * bounded chunks and closes the batch when they run out. Closing is deliberately
 * not something a caller can ask for — it follows from the drain finishing.
 */
export const datasetRunGroupStartBodySchema = z.object({
  groupId: z.string(),
  triggerId: z.string(),
  workflowIds: z.array(z.string()).min(1).optional(),
});
export type DatasetRunGroupStartBody = z.infer<
  typeof datasetRunGroupStartBodySchema
>;

export const datasetRunGroupStartResponseSchema = z.object({
  /** Rows handed to the drain. Zero means nothing was collected. */
  rows: z.number(),
  started: z.boolean(),
});

/**
 * Replays a dataset through the workflows bound to its triggers.
 *
 * Deliberately carries no rows. Naming every row is what `trigger-now` is for,
 * and for a whole dataset that is the browser holding the id set and driving a
 * request loop that dies with the tab. This says only *which workflows*, and the
 * server forgets what each pair has observed so its ordinary evaluation replays
 * the rows — capped, locked and crash-safe, none of which a tab can offer.
 */
export const datasetReplayBodySchema = z.object({
  /** Minted by the caller, and stamped on every run, so the batch is stoppable. */
  groupId: z.string(),
  /** Which bound workflows to replay. Omitted, every workflow on the dataset. */
  workflowIds: z.array(z.string()).min(1).optional(),
  /**
   * Skip rows whose latest run is current, so only rows still owed a run go.
   * Defaulted for clients that predate it.
   */
  onlyNeedsRun: z.boolean().default(false),
});
export type DatasetReplayBody = z.infer<typeof datasetReplayBodySchema>;

/**
 * What the replay stood down.
 *
 * Zero is an ordinary answer rather than an error: a dataset whose triggers have
 * no evaluated pairs has nothing to replay, and saying so is better than
 * reporting a batch that will never move.
 */
export const datasetReplayResponseSchema = z.object({
  /** Pairs whose observed state was discarded. Zero means nothing was replayed. */
  replayedPairs: z.number().int().nonnegative(),
});

export const datasetRetryFailedBodySchema = z.object({
  /**
   * Which bound workflows' failed runs to retry. Omitted, every workflow on the
   * dataset.
   */
  workflowIds: z.array(z.string()).min(1).optional(),
});
export type DatasetRetryFailedBody = z.infer<
  typeof datasetRetryFailedBodySchema
>;

/**
 * A retry-failed is an ack: the work runs in a background saga that streams the
 * failed set, so there is no synchronous count to return. The grid moves each row
 * off `failed` as its new attempt lands.
 */
export const datasetRetryFailedResponseSchema = z.object({
  /** The retry was accepted and is running in the background. */
  started: z.boolean(),
});

export const workflowRunStatsResponseSchema = z.object({
  bucket: z.enum(['hour', 'day']),
  /** Run counts per (time bucket, status), matching the request filters. */
  points: z.array(
    z.object({
      bucketStart: z.string(),
      status: z.string(),
      count: z.number(),
    })
  ),
});

export const workflowAddTriggerResponseSchema = z.object({
  bindingId: z.string(),
  triggerId: z.string(),
  secret: z.string().optional(),
});

export const workflowEventsResponseSchema = z.object({
  /** Per-event payload field names, gathered from emit-event `payloadBindings`. */
  events: z.array(
    z.object({
      eventName: z.string(),
      payloadFields: z.array(z.string()),
    })
  ),
});

export const workflowTriggerNowBodySchema = z.object({
  /** Simulated event payload; spread into the run input like a real emit. */
  payload: z.unknown().optional(),
  /** Dataset row ids to fire for. Required for dataset triggers. */
  rowIds: z.array(z.string()).min(1).optional(),
  /**
   * Narrows the fan-out to these workflows, whatever the trigger's kind.
   * Omitted, every workflow bound to the trigger runs — which is what a real
   * delivery means, and what a test fired from one workflow's editor must not
   * do.
   */
  workflowIds: z.array(z.string()).min(1).optional(),
  /**
   * Ties the runs this call creates to a batch the caller is tracking, so a
   * bulk run started as many requests can be reported as one thing — including
   * by someone who arrives after it started.
   */
  groupId: z.string().optional(),
});
export type WorkflowTriggerNowBody = z.infer<
  typeof workflowTriggerNowBodySchema
>;

export const workflowTriggerNowResponseSchema = z.object({
  dispatchedWorkflowRuns: z.number(),
  runs: z.array(
    z.object({
      runId: z.string(),
      workflowId: z.string(),
    })
  ),
});

export const workflowBindTriggerResponseSchema = z.object({
  bindingId: z.string(),
  triggerType: z.string(),
});

export const workflowSummariesResponseSchema = z.object({
  summaries: z.array(
    z.object({
      workflowId: z.string(),
      triggers: z.array(z.unknown()),
      lastRunAt: z.coerce.date().nullable(),
      lastRunStatus: z.string().nullable(),
    })
  ),
});

export const workflowTriggerSummariesResponseSchema = z.object({
  summaries: z.array(
    z.object({
      triggerId: z.string(),
      workflowCount: z.number(),
      lastFiredAt: z.coerce.date().nullable(),
      hasError: z.boolean(),
      /**
       * Per-workflow breakdown driving the status bars on the trigger row: one
       * entry per bound workflow, its latest run status through this trigger,
       * and how many of its runs in the 24h before that run failed.
       */
      workflows: z.array(
        z.object({
          workflowId: z.string(),
          name: z.string(),
          lastRunStatus: z.string().nullable(),
          failedRuns: z.number(),
          totalRuns: z.number(),
        })
      ),
    })
  ),
});

export const workflowTriggerResponseSchema = z.object({
  runId: z.string().uuid(),
  workflowId: z.string().uuid(),
});

// ---------------------------------------------------------------------------
// Schema classes (canonical v20260601)
// ---------------------------------------------------------------------------

export class WorkflowCreateV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'workflow-create-2026-06-01' as const;

  readonly name = WorkflowCreateV20260601Schema.version;
  readonly schema = workflowCreateBodySchema;
  readonly response = workflowDefinitionResponseSchema;
}

export class WorkflowUpdateV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'workflow-update-2026-06-01' as const;

  readonly name = WorkflowUpdateV20260601Schema.version;
  readonly schema = workflowUpdateBodySchema;
  readonly response = workflowDefinitionResponseSchema;
}

export class WorkflowScheduledAgentUpdateV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version =
    'workflow-scheduled-agent-update-2026-06-01' as const;

  readonly name = WorkflowScheduledAgentUpdateV20260601Schema.version;
  readonly schema = workflowScheduledAgentUpdateBodySchema;
  readonly response = workflowDefinitionResponseSchema;
}

export class WorkflowListRunsV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'workflow-list-runs-2026-06-01' as const;

  readonly name = WorkflowListRunsV20260601Schema.version;
  readonly query = workflowListRunsQuerySchema;
  readonly response = workflowListRunsResponseSchema;
}

export class DatasetAutomationsV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'dataset-automations-2026-06-01' as const;

  readonly name = DatasetAutomationsV20260601Schema.version;
  readonly response = datasetAutomationsResponseSchema;
}

export class DatasetAutomationOverlayV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'dataset-automation-overlay-2026-06-01' as const;

  readonly name = DatasetAutomationOverlayV20260601Schema.version;
  readonly query = datasetAutomationOverlayQuerySchema;
  readonly response = datasetAutomationOverlayResponseSchema;
}

export class WorkflowRunStatsV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'workflow-run-stats-2026-06-01' as const;

  readonly name = WorkflowRunStatsV20260601Schema.version;
  readonly query = workflowRunStatsQuerySchema;
  readonly response = workflowRunStatsResponseSchema;
}

export class WorkflowAddTriggerV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'workflow-add-trigger-2026-06-01' as const;

  readonly name = WorkflowAddTriggerV20260601Schema.version;
  readonly schema = workflowAddTriggerBodySchema;
  readonly response = workflowAddTriggerResponseSchema;
}

export class WorkflowEventsV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'workflow-events-2026-06-01' as const;

  readonly name = WorkflowEventsV20260601Schema.version;
  readonly response = workflowEventsResponseSchema;
}

export class WorkflowTriggerNowV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'workflow-trigger-now-2026-06-01' as const;

  readonly name = WorkflowTriggerNowV20260601Schema.version;
  readonly schema = workflowTriggerNowBodySchema;
  readonly response = workflowTriggerNowResponseSchema;
}

export class DatasetRunGroupCancelV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'dataset-run-group-cancel-2026-06-01' as const;

  readonly name = DatasetRunGroupCancelV20260601Schema.version;
  readonly schema = datasetRunGroupCancelBodySchema;
  readonly response = datasetRunGroupCancelResponseSchema;
}

export class DatasetRunGroupsV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'dataset-run-groups-2026-06-01' as const;

  readonly name = DatasetRunGroupsV20260601Schema.version;
  readonly query = datasetRunGroupsQuerySchema;
  readonly response = datasetRunGroupsResponseSchema;
}

export class DatasetRunGroupCollectV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'dataset-run-group-collect-2026-06-01' as const;

  readonly name = DatasetRunGroupCollectV20260601Schema.version;
  readonly schema = datasetRunGroupCollectBodySchema;
  readonly response = datasetRunGroupCollectResponseSchema;
}

export class DatasetRunGroupStartV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'dataset-run-group-start-2026-06-01' as const;

  readonly name = DatasetRunGroupStartV20260601Schema.version;
  readonly schema = datasetRunGroupStartBodySchema;
  readonly response = datasetRunGroupStartResponseSchema;
}

export class DatasetReplayV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'dataset-replay-2026-06-01' as const;

  readonly name = DatasetReplayV20260601Schema.version;
  readonly schema = datasetReplayBodySchema;
  readonly response = datasetReplayResponseSchema;
}

export class DatasetRetryFailedV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'dataset-retry-failed-2026-06-01' as const;

  readonly name = DatasetRetryFailedV20260601Schema.version;
  readonly schema = datasetRetryFailedBodySchema;
  readonly response = datasetRetryFailedResponseSchema;
}

export class WorkflowUpdateTriggerV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'workflow-update-trigger-2026-06-01' as const;

  readonly name = WorkflowUpdateTriggerV20260601Schema.version;
  readonly schema = workflowUpdateTriggerBodySchema;
  readonly response = okTrueResponseSchema;
}

export class WorkflowRenameTriggerV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'workflow-rename-trigger-2026-06-01' as const;

  readonly name = WorkflowRenameTriggerV20260601Schema.version;
  readonly schema = workflowRenameTriggerBodySchema;
  readonly response = okTrueResponseSchema;
}

export class WorkflowBindTriggerV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'workflow-bind-trigger-2026-06-01' as const;

  readonly name = WorkflowBindTriggerV20260601Schema.version;
  readonly schema = workflowBindTriggerBodySchema;
  readonly response = workflowBindTriggerResponseSchema;
}

export class WorkflowSummariesV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'workflow-summaries-2026-06-01' as const;

  readonly name = WorkflowSummariesV20260601Schema.version;
  readonly schema = workflowSummariesBodySchema;
  readonly response = workflowSummariesResponseSchema;
}

export class WorkflowTriggerSummariesV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'workflow-trigger-summaries-2026-06-01' as const;

  readonly name = WorkflowTriggerSummariesV20260601Schema.version;
  readonly schema = workflowTriggerSummariesBodySchema;
  readonly response = workflowTriggerSummariesResponseSchema;
}

export class WorkflowTriggerV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'workflow-trigger-2026-06-01' as const;

  readonly name = WorkflowTriggerV20260601Schema.version;
  readonly schema = workflowTriggerBodySchema;
  readonly response = workflowTriggerResponseSchema;
}

export class WorkflowDraftTestRunV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'workflow-draft-test-run-2026-06-01' as const;

  readonly name = WorkflowDraftTestRunV20260601Schema.version;
  readonly schema = workflowDraftTestRunBodySchema;
  readonly response = workflowDraftTestRunResponseSchema;
}

// ---------------------------------------------------------------------------
// POST :id/runs/:runId/cancel
// ---------------------------------------------------------------------------

export const workflowCancelRunBodySchema = z.object({});

export type WorkflowCancelRunBody = z.infer<typeof workflowCancelRunBodySchema>;

export const workflowCancelRunResponseSchema = z.object({
  runId: z.string(),
  /** The run's status after the request. */
  status: z.string(),
  /** False when the run was already terminal and nothing was cancelled. */
  cancelled: z.boolean(),
});

export type WorkflowCancelRunResponse = z.infer<
  typeof workflowCancelRunResponseSchema
>;

export class WorkflowCancelRunV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'workflow-cancel-run-2026-06-01' as const;

  readonly name = WorkflowCancelRunV20260601Schema.version;
  readonly schema = workflowCancelRunBodySchema;
  readonly response = workflowCancelRunResponseSchema;
}

// ---------------------------------------------------------------------------
// POST :id/runs/:runId/retry
// ---------------------------------------------------------------------------

export const workflowRetryRunBodySchema = z.object({});

export type WorkflowRetryRunBody = z.infer<typeof workflowRetryRunBodySchema>;

export const workflowRetryRunResponseSchema = z.object({
  /** The NEW run's id; the run being retried is left untouched. */
  runId: z.string(),
  workflowId: z.string(),
  retriedFromRunId: z.string(),
  /** 0-based attempt index of the new run within its retry chain. */
  retryNum: z.number(),
});

export type WorkflowRetryRunResponse = z.infer<
  typeof workflowRetryRunResponseSchema
>;

export class WorkflowRetryRunV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'workflow-retry-run-2026-06-01' as const;

  readonly name = WorkflowRetryRunV20260601Schema.version;
  readonly schema = workflowRetryRunBodySchema;
  readonly response = workflowRetryRunResponseSchema;
}

// ---------------------------------------------------------------------------
// POST :id/runs/:runId/steps/:nodeId/clear-debounce
// ---------------------------------------------------------------------------

/**
 * Releases what a debounce node is holding for the key this step was
 * suppressed on, so the next evaluation lets it through.
 *
 * Addressed by run step rather than by claim: the caller is looking at a
 * stopped step and means "this one". The definition node id the claim is keyed
 * on, and the key itself, are both resolved server-side from the step — the
 * client is never told the definition id (the run graph strips it) and should
 * not be trusted to name the key.
 */
export const workflowClearDebounceBodySchema = z.object({});

export type WorkflowClearDebounceBody = z.infer<
  typeof workflowClearDebounceBodySchema
>;

export const workflowClearDebounceResponseSchema = z.object({
  /** The key that was released, as resolved from the step's recorded input. */
  key: z.string(),
  /** 0 when nothing was being held, so the key was already eligible. */
  cleared: z.number(),
});

export type WorkflowClearDebounceResponse = z.infer<
  typeof workflowClearDebounceResponseSchema
>;

export class WorkflowClearDebounceV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'workflow-clear-debounce-2026-06-01' as const;

  readonly name = WorkflowClearDebounceV20260601Schema.version;
  readonly schema = workflowClearDebounceBodySchema;
  readonly response = workflowClearDebounceResponseSchema;
}

// ---------------------------------------------------------------------------
// POST :id/runs/:runId/steps/:nodeId/resume
// ---------------------------------------------------------------------------

/**
 * Completes a workflow run step that a tool parked awaiting an external
 * continuation, and resumes the run.
 *
 * The chat-side equivalent is `POST /resource/chat/:id/tools/:toolCallId/
 * run-interrupted`. Without this, a client-implemented (`client/*`) tool
 * dropped into a ToolCall node has no way to ever be completed.
 */
export const workflowResumeStepBodySchema = z.object({
  /** Output to complete the parked step with; flows to downstream nodes. */
  output: z.record(z.string(), z.unknown()),
  /** Fan-out instance to complete. Defaults to the run's root instance. */
  instanceKey: z.string().optional(),
});

export type WorkflowResumeStepBody = z.infer<
  typeof workflowResumeStepBodySchema
>;

export const workflowResumeStepResponseSchema = z.object({
  runId: z.string(),
  nodeId: z.string(),
  instanceKey: z.string(),
  /** False when the step was not parked (already completed, or still running). */
  resumed: z.boolean(),
});

export type WorkflowResumeStepResponse = z.infer<
  typeof workflowResumeStepResponseSchema
>;

export class WorkflowResumeStepV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'workflow-resume-step-2026-06-01' as const;

  readonly name = WorkflowResumeStepV20260601Schema.version;
  readonly schema = workflowResumeStepBodySchema;
  readonly response = workflowResumeStepResponseSchema;
}

export class WorkflowRunsByWorkflowsV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'workflow-runs-by-workflows-2026-06-01' as const;

  readonly name = WorkflowRunsByWorkflowsV20260601Schema.version;
  readonly schema = workflowRunsByWorkflowsBodySchema;
  readonly response = workflowRunsByWorkflowsResponseSchema;
}

export const WORKFLOW_API_SCHEMAS = [
  WorkflowCreateV20260601Schema,
  WorkflowUpdateV20260601Schema,
  WorkflowAddTriggerV20260601Schema,
  WorkflowEventsV20260601Schema,
  WorkflowTriggerNowV20260601Schema,
  WorkflowScheduledAgentUpdateV20260601Schema,
  WorkflowListRunsV20260601Schema,
  WorkflowRunStatsV20260601Schema,
  DatasetAutomationsV20260601Schema,
  DatasetAutomationOverlayV20260601Schema,
  DatasetRunGroupCancelV20260601Schema,
  DatasetReplayV20260601Schema,
  DatasetRetryFailedV20260601Schema,
  WorkflowUpdateTriggerV20260601Schema,
  WorkflowRenameTriggerV20260601Schema,
  WorkflowBindTriggerV20260601Schema,
  WorkflowSummariesV20260601Schema,
  WorkflowTriggerSummariesV20260601Schema,
  WorkflowRunsByWorkflowsV20260601Schema,
  WorkflowTriggerV20260601Schema,
  WorkflowDraftTestRunV20260601Schema,
  WorkflowCancelRunV20260601Schema,
  WorkflowRetryRunV20260601Schema,
  WorkflowResumeStepV20260601Schema,
] as const satisfies readonly ApiSchemaClass[];
